import { useQuery } from '@apollo/client';
import type { FetchAlertBannersResponse } from '@generated/graphql';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { UpdateOrganizationSwitch } from '@ovComponents/4-module/updateOrganizationSwitch/updateOrganizationSwitch';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { usePageState } from '@utils/usePageState';
import { Box, Button, Skeleton } from 'hephaestus/web';
import { Add as AddIcon } from 'hephaestus/webIcons';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FETCH_ALERT_BANNERS } from './alertBanners.queries';
import AlertBannerFilterModal from './components/alertBannerFilterModal';
import { BannerCell } from './components/bannerCell';
import {
  BASE_TABLE,
  DEFAULT_PAGE_SIZE,
  sortableTableHeaderKeyMap,
  TableHeaderKeys,
} from './constants';
import { useAlertBannerPermissions } from './hooks/useAlertBannerPermissions';
import { type AlertBannerRow, AlertBannerStatus } from './types';
import { getAlertBannerStatus } from './utils';

export const AlertBanners = () => {
  const navigate = useNavigate();
  const canWriteAdvancedOrganizationSettings = usePermission(
    'write:advanced_organization_settings',
  );
  const canWriteAlertBanners = usePermission('write:alert_banners');
  const canTransitionAlertBanners = usePermission('transition:alert_banners');
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['alertBanners']);

  const [page, setPage] = usePageState(1, 'page');
  const [sortField, setSortField] = usePageState('createdAt', 'sf');
  const [sortDesc, setSortDesc] = usePageState(true, 'sd');
  const [pageSize, setPageSize] = usePageState(DEFAULT_PAGE_SIZE, 'ps');
  const [filteredStatuses, setFilteredStatuses] = useState<AlertBannerStatus[]>(
    Object.values(AlertBannerStatus),
  );

  const { loading, data, refetch } = useQuery<{
    fetchAlertBanners: FetchAlertBannersResponse;
  }>(FETCH_ALERT_BANNERS, {
    variables: {
      input: {
        filter: {
          statuses: filteredStatuses,
          organization: activeOrganization.id,
        },
        pagination: {
          sortField,
          sortDesc,
          perPage: pageSize,
          offSet: (page - 1) * pageSize,
        },
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const { hasReadBannersPermission, hasWriteBannersPermission } =
    useAlertBannerPermissions();
  const hasWriteAdvancedOrgSettingsPermission =
    canWriteAdvancedOrganizationSettings;

  const totalCount = data?.fetchAlertBanners.totalCount;
  const alertBanners: AlertBannerRow[] | undefined =
    data?.fetchAlertBanners.alertBanners.map((banner) => ({
      ...banner,
      status: getAlertBannerStatus(
        banner.state,
        banner.startDate,
        banner.endDate,
      ),
    }));

  const onSort = (field: string) => {
    if (sortField === field) {
      setSortDesc(!sortDesc);
    }
    setSortField(field);
  };

  const sortDirection = (field?: string) =>
    sortField === field ? (sortDesc ? 'desc' : 'asc') : undefined;

  const permissionLookup: Record<string, boolean> = {
    'write:alert_banners': canWriteAlertBanners,
    'transition:alert_banners': canTransitionAlertBanners,
  };

  const tableHeaders = BASE_TABLE.filter(
    (header) =>
      !header.permissions?.length ||
      header.permissions.some(
        (requiredPermission) => permissionLookup[requiredPermission],
      ),
  );

  if (!hasWriteBannersPermission && !hasReadBannersPermission) return null;
  return (
    <Card loading={loading}>
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          {hasWriteAdvancedOrgSettingsPermission && (
            <UpdateOrganizationSwitch
              field='useParentAlertBanners'
              label={t('useParentAlertBanners')}
              organizationId={activeOrganization.id}
              refetchQueries={[FETCH_ALERT_BANNERS]}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <AlertBannerFilterModal
              filterExists={
                filteredStatuses.length !==
                Object.values(AlertBannerStatus).length
              }
              initialStatuses={filteredStatuses}
              onApply={setFilteredStatuses}
            />
            {hasWriteBannersPermission && (
              <Button component={Link} to='./new' startIcon={<AddIcon />}>
                {t('table.newBanner')}
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableBody>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableHeadCell
                key={header.key}
                isSortable={header.isSortable}
                onClick={() =>
                  header.isSortable && sortableTableHeaderKeyMap[header.key]
                    ? onSort(sortableTableHeaderKeyMap[header.key]!)
                    : undefined
                }
                sortDirection={sortDirection(
                  sortableTableHeaderKeyMap[header.key],
                )}
                right={[
                  TableHeaderKeys.Status,
                  TableHeaderKeys.Organization,
                ].includes(header.key)}
                center={[
                  TableHeaderKeys.RoleProfiles,
                  TableHeaderKeys.LastUpdated,
                  TableHeaderKeys.ActiveDateRange,
                  TableHeaderKeys.Actions,
                ].includes(header.key)}
              >
                {t(header.labelPath)}
              </TableHeadCell>
            ))}
          </TableRow>
          {loading
            ? [...Array(15)].map((_x, i: number) => (
                <TableRow key={`skeleton-${i}`}>
                  {tableHeaders.map((header, idx) => (
                    <TableCell
                      key={`skeleton-${i}-${header.key}`}
                      dense
                      isFirst={idx === 0}
                    >
                      <Skeleton width='100%' height='16px' />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : alertBanners?.map((banner) => (
                <TableRow
                  key={banner.id}
                  hover
                  sx={{
                    '&:hover': {
                      cursor:
                        banner.organization.id === activeOrganization.id
                          ? 'pointer'
                          : 'default',
                    },
                  }}
                  onClick={() => {
                    if (
                      banner.organization.id === activeOrganization.id &&
                      (hasReadBannersPermission || hasWriteBannersPermission)
                    ) {
                      navigate(`/settings/alertBanners/${banner.id}`);
                    }
                  }}
                >
                  {tableHeaders.map((header, idx) => (
                    <BannerCell
                      key={header.key}
                      headerKey={header.key}
                      alertBanner={banner}
                      isFirst={idx === 0}
                      refetch={refetch}
                    />
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      {!!totalCount && totalCount > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            my: 2,
          }}
        >
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={(_e, newPage) => setPage(newPage)}
            perPage={pageSize}
            onChangePerPage={(newPageSize) => setPageSize(newPageSize)}
            disabled={loading}
            size='small'
          />
        </Box>
      )}
    </Card>
  );
};
